import { useEffect, useMemo, useRef, useCallback, type ReactNode } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  Button,
  mergeClasses,
  Text,
  Tooltip,
} from '@coreai/fluentui-react';
import {
  DismissRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
} from '@fluentui/react-icons';
import { InputOutputDetail, type DetailProperty } from '../InputOutputDetail/InputOutputDetail';
import { ResizablePanel } from '@/components/WorkflowDesigner/ResizablePanel';
import { useTelemetry } from '@/hooks/useTelemetry';
import type { NodeExecution, RequestContentSide, RequestContentRetryTarget } from '@/types/monitoring';
import { useStyles } from './MonitoringExpandDialog.styles';
import {
  executionStatusIcon,
  formatExecutionDuration,
  formatExecutionTimestamp,
} from '@/utils/monitoring';
import { ExecutionLog, type ExecutionLogProps } from '../BottomPanel/ExecutionLog';

type MonitoringExpandDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  /** All node executions in the current run (drives the spine). */
  nodeExecutions: NodeExecution[];
  /** Currently selected node execution. Drives the panes + properties strip. */
  selectedNodeExecution: NodeExecution | null;
  onRetryRequest?: ((side: RequestContentSide) => void) | undefined;
  retryingRequestSide?: RequestContentRetryTarget | undefined;
  /** Called when the spine selects a different action. */
  onSelectNode: (nodeId: string) => void;
  /** For agent actions: current iteration index. */
  selectedIterationIndex?: number;
  /** For agent actions: total iteration count. */
  iterationCount?: number;
  /** Called when iteration changes. */
  onIterationChange?: (index: number) => void;
  /** Pre-resolved input for the current selection (handles iteration overlay). */
  displayInput?: unknown;
  /** Pre-resolved output for the current selection. */
  displayOutput?: unknown;
  /**
   * Identity overrides for the currently-selected item. When the host is
   * showing a *child* of `selectedNodeExecution` (e.g. an agent tool
   * action), pass these so the dialog header / status pill / properties
   * strip / download-name reflect the actual selected item rather than
   * the parent execution.
   */
  displayName?: string | undefined;
  displayStatus?: NodeExecution['status'] | undefined;
  displayDuration?: number | undefined;
  displayStartTime?: string | undefined;
  displayEndTime?: string | undefined;
  displayNodeId?: string | undefined;
  displayNodeType?: string | undefined;
  /**
   * Data + handlers for the shared execution-log tree. When provided, the
   * dialog's left rail renders the full LogsPanel tree instead of the
   * basic flat spine. Lets the dialog drill into agents, scopes,
   * iterations, tool actions, and chat messages just like LogsPanel.
   */
  executionLog?: ExecutionLogProps | undefined;
  /** Optional header command rendered before iteration navigation (e.g. "Submit from this action"). */
  headerAction?: ReactNode;
  notification?: ReactNode;
};

export const MonitoringExpandDialog = ({
  isOpen,
  onClose,
  nodeExecutions,
  selectedNodeExecution,
  onRetryRequest,
  retryingRequestSide,
  onSelectNode,
  selectedIterationIndex,
  iterationCount,
  onIterationChange,
  displayInput,
  displayOutput,
  displayName,
  displayStatus,
  displayDuration,
  displayStartTime,
  displayEndTime,
  displayNodeId,
  displayNodeType,
  executionLog,
  headerAction,
  notification,
}: MonitoringExpandDialogProps) => {
  const styles = useStyles();
  const { trackEvent } = useTelemetry();
  const openedAtRef = useRef<number | null>(null);
  const triggerElRef = useRef<Element | null>(null);

  // Capture the element that had focus before open so we can restore it after close
  useEffect(() => {
    if (isOpen) {
      triggerElRef.current = document.activeElement;
      openedAtRef.current = Date.now();
      trackEvent('monitoring_expand_dialog_opened', {
        action_type: selectedNodeExecution?.nodeType ?? null,
        had_iterations: !!iterationCount && iterationCount > 1,
        run_action_count: nodeExecutions.length,
      });
    } else if (openedAtRef.current) {
      const dwell = Date.now() - openedAtRef.current;
      trackEvent('monitoring_expand_dialog_closed', { dwell_ms: dwell });
      openedAtRef.current = null;
      // Restore focus after the dialog has fully unmounted
      const target = triggerElRef.current;
      if (target instanceof HTMLElement) {
        requestAnimationFrame(() => target.focus());
      }
    }
    // intentionally not depending on every prop — we want one event per open/close
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Effective identity of the selected item. Honors child overrides
  // (e.g. selected tool action) so the dialog header/status/duration/etc
  // describe the actual selected item, not the parent execution.
  const effectiveName = displayName ?? selectedNodeExecution?.nodeName ?? 'No action selected';
  const effectiveStatus = displayStatus ?? selectedNodeExecution?.status ?? 'pending';
  const effectiveDuration = displayDuration !== undefined ? displayDuration : selectedNodeExecution?.duration;
  const effectiveStartTime = displayStartTime ?? selectedNodeExecution?.startTime;
  const effectiveEndTime = displayEndTime ?? selectedNodeExecution?.endTime;
  const effectiveNodeId = displayNodeId ?? selectedNodeExecution?.nodeId;
  const effectiveNodeType = displayNodeType ?? selectedNodeExecution?.nodeType;
  const downloadStem = displayName ?? selectedNodeExecution?.nodeName ?? 'action';

  const statusClass = useMemo(() => {
    if (effectiveStatus === 'succeeded') return styles.statusSucceeded;
    if (effectiveStatus === 'failed') return styles.statusFailed;
    if (effectiveStatus === 'running') return styles.statusRunning;
    return styles.statusOther;
  }, [effectiveStatus, styles]);

  const onSpineClick = useCallback((nodeId: string) => {
    onSelectNode(nodeId);
    trackEvent('monitoring_expand_dialog_spine_action_switched', { node_id: nodeId });
  }, [onSelectNode, trackEvent]);

  const hasIterations = !!iterationCount && iterationCount > 1;
  const currentIter = typeof selectedIterationIndex === 'number' ? selectedIterationIndex : 0;
  const canPrev = hasIterations && currentIter > 0;
  const canNext = hasIterations && currentIter < (iterationCount ?? 1) - 1;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) onClose();
      }}
      modalType="modal"
    >
      <DialogSurface className={styles.surface} aria-label="Action details expanded">
        <DialogBody className={styles.body}>
          <div className={styles.header}>
            <div className={styles.headerTitle}>
              <Text className={styles.crumb}>Action details</Text>
              <Text className={styles.actionName}>
                {effectiveName}
              </Text>
            </div>
            <div className={styles.headerRight}>
              {headerAction}
              {hasIterations && (
                <div className={styles.iterationNav} role="group" aria-label="Iteration navigation">
                  <Tooltip content="Previous iteration" relationship="label">
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<ChevronLeftRegular />}
                      disabled={!canPrev}
                      onClick={() => onIterationChange?.(Math.max(0, currentIter - 1))}
                      aria-label="Previous iteration"
                    />
                  </Tooltip>
                  <span className={styles.iterationLabel}>
                    {currentIter + 1} / {iterationCount}
                  </span>
                  <Tooltip content="Next iteration" relationship="label">
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<ChevronRightRegular />}
                      disabled={!canNext}
                      onClick={() => onIterationChange?.(Math.min((iterationCount ?? 1) - 1, currentIter + 1))}
                      aria-label="Next iteration"
                    />
                  </Tooltip>
                </div>
              )}
              <Tooltip content="Close" relationship="label">
                <Button
                  appearance="subtle"
                  icon={<DismissRegular />}
                  onClick={onClose}
                  aria-label="Close"
                />
              </Tooltip>
            </div>
          </div>

          {notification}
          <div className={styles.layout}>
            <ResizablePanel
              direction="horizontal"
              defaultSize={280}
              minSize={200}
              maxSize={520}
              resizerPosition="end"
            >
              <nav className={styles.spine} aria-label="Execution spine">
                <div className={styles.spineHeading}>Actions in run</div>
                {executionLog ? (
                  // Same tree used by LogsPanel — drills into agents, scopes,
                  // iterations, tool actions, and chat messages.
                  // We override `setSelectedChatMessage` with a no-op:
                  // LogsPanel renders chat-message detail in its right pane,
                  // but the dialog only has Output/Input/Properties tabs and
                  // no chat-content view. Letting chat rows be selected
                  // would highlight the row while the panes still showed
                  // the previous action's data — confusing UX. Disabling
                  // the handler keeps the row visually inert (no highlight,
                  // no pane mismatch); chat rows still expand/collapse via
                  // their own toggle.
                  <ExecutionLog
                    {...executionLog}
                    setSelectedChatMessage={undefined}
                  />
                ) : (
                  // Fallback: simple flat list when the host hasn't wired up
                  // the full execution-log data. Mirrors the pre-refactor
                  // behaviour so the dialog still renders something useful
                  // in isolation (e.g. older callers or tests).
                  nodeExecutions
                    .filter((n) => !n.parentNodeId)
                    .map((n) => (
                      <button
                        key={n.nodeId}
                        type="button"
                        className={mergeClasses(
                          styles.spineItem,
                          n.nodeId === selectedNodeExecution?.nodeId && styles.spineItemActive
                        )}
                        onClick={() => onSpineClick(n.nodeId)}
                        aria-current={n.nodeId === selectedNodeExecution?.nodeId ? 'true' : undefined}
                      >
                        <span className={styles.statusDot}>
                          {executionStatusIcon(n.status, n.status === 'succeeded' ? styles.statusSucceeded
                            : n.status === 'failed' ? styles.statusFailed
                            : n.status === 'running' ? styles.statusRunning
                            : styles.statusOther)}
                        </span>
                        <span className={styles.spineName}>{n.nodeName}</span>
                        <span className={styles.spineDur}>{formatExecutionDuration(n.duration)}</span>
                      </button>
                    ))
                )}
              </nav>
            </ResizablePanel>

            <div className={styles.paneArea}>
              <InputOutputDetail
                input={displayInput !== undefined ? displayInput : selectedNodeExecution?.input ?? null}
                requestContent={selectedNodeExecution?.requestContent}
                onRetryRequest={onRetryRequest}
                retryingRequestSide={retryingRequestSide}
                output={displayOutput !== undefined ? displayOutput : selectedNodeExecution?.output ?? null}
                downloadStem={downloadStem}
                ariaLabel="Action properties"
                // Retry history belongs to the selected action itself. When a
                // child (e.g. a tool action) is shown via display* overrides,
                // suppress it — the parent's attempts don't describe the child.
                retryHistory={displayName ? undefined : selectedNodeExecution?.retryHistory}
                properties={[
                  {
                    label: 'Status',
                    value: effectiveStatus.charAt(0).toUpperCase() + effectiveStatus.slice(1),
                    valueClassName: statusClass,
                  },
                  {
                    label: 'Duration',
                    value: formatExecutionDuration(effectiveDuration),
                  },
                  {
                    label: 'Started',
                    value: formatExecutionTimestamp(effectiveStartTime),
                    title: effectiveStartTime,
                  },
                  {
                    label: 'Ended',
                    value: formatExecutionTimestamp(effectiveEndTime),
                    title: effectiveEndTime,
                  },
                  {
                    label: 'Node ID',
                    value: effectiveNodeId ?? '—',
                    title: effectiveNodeId,
                  },
                  {
                    label: 'Type',
                    value: effectiveNodeType ?? '—',
                  },
                ] satisfies DetailProperty[]}
              />
            </div>
          </div>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
